import { useState, useContext, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import NotFoundStatusContext from "./notFoundStatusContext";
import HomepageMerge from "./Components/Homepage/HomepageMerge/HomepageMerge";
import AboutusMerge from "./Components/AboutusPage/AboutusMerge/AboutusMerge";
import JobPostDetail from "./Components/JobPostDetailPage/JobPostDetail";
import OurTeamMerge from "./Components/OurteamPage/OurTeamMerge/OurTeamMerge";
import ApplyNowPageMerge from "./Components/ApplyNowPage/ApplyNowPageMerge/ApplyNowPageMerge";
import PortfolioPageMerge from "./Components/PortfolioPage/PortfolioPageMerge/PortfolioPageMerge";
import PortFolioDetail from "./Components/PortfolioDetail/PortfolioDetail";
//import AWSdetailMerge from "./Components/AWSdetailPage/AWSdetailMerge/AWSdetailMerge";
import CaseStudiesMerge from "./Components/CaseStudiesPage/CaseStudiesMerge/CaseStudiesMerge";
import CaseStudyDetailMerge from "./Components/CaseStudyDetailPage/CaseStudyDetailMerge/CaseStudyDetailMerge";
import BlogsMerge from "./Components/Blogs/BlogsMerge/BlogsMerge";
import BlogsDetailmerge from "./Components/BlogsDetailPage/BlogsDetailmerge/BlogsDetailmerge";
//----------------------import SignIn from "./Components/Sign/SignIn";
// Stylesheets of the lazily loaded admin pages. They are imported here, in the position their (now lazy)
// components used to occupy, ONLY to keep the public pages looking exactly as before: CRA does not scope CSS, and
// several of these files define rules for classes the public site also uses (.main_div_aboutusnavbar,
// .Heading_navbar, :root variables, body, ...). Removing them would silently restyle public pages. The JS of those
// pages (CKEditor, Quill, the dashboard) is what was heavy, and that is lazy now. Cleaning up the collisions
// (scoping each admin stylesheet) is a separate, visual-regression-tested follow-up.
// (was: SignInPage, Dashboard, Category, Tags)
import "./Components/SignInPage/SignInPage.css";
import "./Components/Sign/Dashboard.css";
import "./Components/Sign/Loader.css";
import "./Components/Sign/Category.css";
import "./Components/Sign/Tags.css";
import Faqs from "./Components/Homepage/Faqs/Faqs";
import WebsiteDeploymentServicesMerge from "./Components/WebsiteDeploymentServices/WebsiteDeploymentServicesMerge/WebsiteDeploymentServicesMerge";
//import MobileDevelopmentServiceMerge from "./Components/MobileDevelopementService/MobileDevelopementServiceMerge/MobileDevelopementServiceMerge";
import Tips from "./Components/Homepage/Tips/Tips";
import TipDetailMerge from "./Components/Homepage/Tips/TipDetailMerge";
import NotFound from "./Components/Homepage/Tips/NotFound";
// (scoping each admin stylesheet) is a separate, visual-regression-tested follow-up.
// (was: dashboard add/edit pages)
import "./Components/Dashboard/AddJobPost/AddJobPost.css";
import "./Components/Dashboard/DashboardLayout/DashboardLayout.css";
import "./Components/Dashboard/AddTeamMember/AddTeamMember.css";
import "./Components/Dashboard/AddEventGallery/AddEventGallery.css";
import "./Components/Dashboard/AddCaseStudy/AddCaseStudy.css";
import "./Components/Dashboard/AddPortfolio/AddPortfolio.css";
import "./Components/Dashboard/ResumeReceived/ResumeReceived.css";
import "./Components/Dashboard/AddTestimonial/AddTestimonial.css";
import "./Components/Dashboard/AddTips/AddTips.css";
// Visual theme for the whole admin panel; every rule inside is scoped to .dashboard-layout. Keep it last.
import "./Components/Dashboard/adminTheme.css";
import Appointment from "./Components/Homepage/Appointment/Appointment";
import OurServicesMerge from "./Components/our services/OurServicesMerge";
import DigitalCommerceServicesMerge from "./Components/DigitalCommerceSolution/DigitalCommerceServicesMerge";
import CRMPlatformServicesMerge from "./Components/CRMPlatformSolution/CRMPlatformServicesMerge";
import GenerativeAIServicesMerge from "./Components/GenerativeAISolution/GenerativeAIServicesMerge";
import MachineLearningServicesMerge from "./Components/MachineLearningSolution/MachineLearningServicesMerge";
import SoftwareEngineeringServicesMerge from "./Components/SoftwareEngineeringSolution/SoftwareEngineeringServicesMerge";
import CloudEngineeringServicesMerge from "./Components/CloudEngineeringSolution/CloudEngineeringServicesMerge";
import DataEngineeringServicesMerge from "./Components/DataEngineeringSolution/DataEngineeringServicesMerge";
import RPAIPAServicesMerge from "./Components/RPAIPASolution/RPAIPAServicesMerge";
import ProductStrategyConsultingServicesMerge from "./Components/ProductStrategyConsultingSolution/ProductStrategyConsultingServicesMerge";
import GraphicsDesigningServicesMerge from "./Components/GraphicsDesigningSolution/GraphicsDesigningServicesMerge";
import SystemIntegrationServicesMerge from "./Components/SystemIntegrationSolution/SystemIntegrationServicesMerge";
import PrivacyPolicy from "./Components/PrivacyPolicy/PrivacyPolicy";
import ContactPage from "./Components/ContactPage/ContactPage";
// (scoping each admin stylesheet) is a separate, visual-regression-tested follow-up.
// (was: AppointmentsReceived)
import "./Components/AppointmentsReceived/AppointmentsReceived.css";
import "./Components/Dashboard/DashboardLayout/DashboardLayout.css";
import WhatsAppIcon from "./Components/WhatsAppIcon/WhatsAppIcon";
import ProtectedRoute from "./Components/Auth/ProtectedRoute";
import ErrorBoundary from "./Components/ErrorBoundary/ErrorBoundary";
import './styles/typeScale.css';

// Admin dashboard and legacy blog-listing pages are loaded on demand. They pull in CKEditor and Quill (about
// 1.5 MB of the old single bundle, plus a 156 KB <style> that CKEditor injects into every page), none of which a
// public visitor needs. They are never prerendered, so lazy loading cannot cause a hydration mismatch; the public
// pages below stay eagerly imported on purpose (a lazy chunk would not be ready when a prerendered page hydrates).
const SignInPage = lazy(() => import("./Components/SignInPage/SignInPage"));
const Dashboard = lazy(() => import("./Components/Sign/Dashboard"));
const Category = lazy(() => import("./Components/Sign/Category"));
const Tags = lazy(() => import("./Components/Sign/Tags"));
const AddJobPost = lazy(() => import("./Components/Dashboard/AddJobPost/AddJobPost"));
const AddTeamMember = lazy(() => import("./Components/Dashboard/AddTeamMember/AddTeamMember"));
const AddEventGallery = lazy(() => import("./Components/Dashboard/AddEventGallery/AddEventGallery"));
const AddCaseStudy = lazy(() => import("./Components/Dashboard/AddCaseStudy/AddCaseStudy"));
const AddPortfolio = lazy(() => import("./Components/Dashboard/AddPortfolio/AddPortfolio"));
const ResumeReceived = lazy(() => import("./Components/Dashboard/ResumeReceived/ResumeReceived"));
const AddTestimonial = lazy(() => import("./Components/Dashboard/AddTestimonial/AddTestimonial"));
const AddTips = lazy(() => import("./Components/Dashboard/AddTips/AddTips"));
const AddFaq = lazy(() => import("./Components/Dashboard/AddFaq/AddFaq"));
const AppointmentsReceived = lazy(() => import("./Components/AppointmentsReceived/AppointmentsReceived"));
const NewsletterSubscribers = lazy(() => import("./Components/Sign/NewsletterSubscribers"));

// Client-side stand-in for a 301 on renamed top-level routes (old URL -> new URL, query kept).
// A real server-side 301 for these is a deployment task; this only covers in-app/JS visits.
function RedirectKeepSearch({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

// Suspense boundary for the lazy pages only. It must NOT wrap the public routes: the prerendered HTML has no
// Suspense markers, so a boundary around a hydrating tree makes React discard the server HTML (error #418).
function Lazy({ children }) {
  return <Suspense fallback={<div aria-busy="true" style={{ minHeight: "60vh" }} />}>{children}</Suspense>;
}

// The legacy /blogs/:id/:title page duplicated the blog detail page (and its post fetch was broken),
// so those URLs now forward to the canonical /blog/detail/:postId/:title. Client-side only; the HTTP
// 301 for /blogs/:id/:title is a server task (see SEO_AUDIT_COMPLETION_STATUS.md).
function LegacyBlogPostRedirect() {
  const { id, title } = useParams();
  return <Navigate to={`/blog/detail/${id}/${title}`} replace />;
}

// Site-wide fallback metadata. Any page that renders its own <Helmet> with the same
// tag (og:*, canonical, robots, twitter:*) correctly overrides these — react-helmet
// merges across nested Helmet instances, and deeper/later-rendered instances win.
// Pages without their own Helmet fall back to these instead of showing blank/generic
// previews when shared, an unindexed-by-default page, or no canonical at all.
function GlobalSeoDefaults() {
  const location = useLocation();
  const { isNotFoundRoute } = useContext(NotFoundStatusContext);
  const canonicalUrl = `https://360synergytech.com${location.pathname}`;

  return (
    <Helmet>
      <meta property="og:title" content="360 SynergyTech | Custom Software, AI & Cloud Solutions" />
      <meta property="og:description" content="360 SynergyTech provides innovative IT solutions including web development, AI solutions, cloud services, and software engineering tailored for businesses worldwide." />
      <meta property="og:image" content="https://360synergytech.com/synergy-logo.png" />
      {/* No og:url on the 404 page: it must not point at the homepage or at a dead URL. */}
      {!isNotFoundRoute && <meta property="og:url" content={canonicalUrl} />}

      {/* The 404 page is intentionally canonical-less; NotFound.js flags itself via
          NotFoundStatusContext so this fallback doesn't self-reference a dead URL. */}
      {!isNotFoundRoute && <link rel="canonical" href={canonicalUrl} />}
      <meta name="robots" content="index, follow" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="360 SynergyTech | Custom Software, AI & Cloud Solutions" />
      <meta name="twitter:description" content="360 SynergyTech provides innovative IT solutions including web development, AI solutions, cloud services, and software engineering tailored for businesses worldwide." />
      <meta name="twitter:image" content="https://360synergytech.com/synergy-logo.png" />
    </Helmet>
  );
}

function App() {
  const [isNotFoundRoute, setIsNotFoundRoute] = useState(false);

  return (
    <Router>
      <NotFoundStatusContext.Provider value={{ isNotFoundRoute, setIsNotFoundRoute }}>
      <GlobalSeoDefaults />
      <WhatsAppIcon />
      <ErrorBoundary>
      <Routes>




        {/* Main Routes */}
        <Route path="/" element={<HomepageMerge />} />
        <Route path="/about" element={<AboutusMerge />} />
        <Route path="/aboutus" element={<RedirectKeepSearch to="/about" />} />
        <Route path="/services" element={<OurServicesMerge />} />
        <Route path="/faqs" element={<Faqs isFaqPage={true} />} />
        <Route path="/job/description/:jobId" element={<JobPostDetail />} />
        <Route path="/job/description/:jobId/:title" element={<JobPostDetail />} />
        <Route path="/team" element={<OurTeamMerge />} />
        <Route path="/careers" element={<ApplyNowPageMerge />} />
        <Route path="/applynow" element={<RedirectKeepSearch to="/careers" />} />
        <Route path="/appointment" element={<Appointment isAppointmentPage={true} />} />

        {/* Portfolio Routes */}
        <Route path="/portfolio" element={<PortfolioPageMerge />} />
        <Route path="/portfolio/detail/:id/:title" element={<PortFolioDetail />} />
        {/* <Route path="/dashboard/add-portfolio" element={<Lazy><AddPortfolio /></Lazy>} /> */}

        {/* Services Routes */}
        {/* <Route path="/aws" element={<AWSdetailMerge />} /> */}
        <Route path="/custom-software-development" element={<WebsiteDeploymentServicesMerge />} />
        <Route path="/digital-commerce" element={<DigitalCommerceServicesMerge />} />
        <Route path="/crm-platform-implementation" element={<CRMPlatformServicesMerge />} />
        <Route path="/generative-ai" element={<GenerativeAIServicesMerge />} />
        <Route path="/machine-learning" element={<MachineLearningServicesMerge />} />
        <Route path="/software-engineering" element={<SoftwareEngineeringServicesMerge />} />
        <Route path="/cloud-engineering" element={<CloudEngineeringServicesMerge />} />
        <Route path="/data-engineering" element={<DataEngineeringServicesMerge />} />
        <Route path="/rpa-ipa-implementation" element={<RPAIPAServicesMerge />} />
        <Route path="/product-strategy-consulting" element={<ProductStrategyConsultingServicesMerge />} />
        <Route path="/graphics-designing" element={<GraphicsDesigningServicesMerge />} />
        <Route path="/system-integration" element={<SystemIntegrationServicesMerge />} />
        {/* <Route path="/mobile/app/developement" element={<MobileDevelopmentServiceMerge />} /> */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Case Studies Routes */}
        <Route path="/case-studies" element={<CaseStudiesMerge />} />
        <Route path="/case/studies" element={<RedirectKeepSearch to="/case-studies" />} />
        <Route path="/case/study/detail/:id/:title" element={<CaseStudyDetailMerge />} />

        {/* Blog and Tips Routes */}
        <Route path="/tips" element={<Tips isTipsPage={true} />} />
        <Route path="/tips/:id/:title" element={<TipDetailMerge />} />
        <Route path="/blog" element={<BlogsMerge />} />
        <Route path="/blogs" element={<RedirectKeepSearch to="/blog" />} />
        <Route path="/blog/detail/:postId/:title" element={<BlogsDetailmerge />} />
        <Route path="/blogs/:id/:title" element={<LegacyBlogPostRedirect />} />
        <Route path="/category/:id" element={<Lazy><Category /></Lazy>} />
        <Route path="/category/:id/:name" element={<Lazy><Category /></Lazy>} />
        <Route path="/tags/:id" element={<Lazy><Tags /></Lazy>} />
        <Route path="/tags/:id/:name" element={<Lazy><Tags /></Lazy>} />

        {/* Authentication Routes */}


        
        {/* <Route path="/login" element={<Lazy><SignInPage /></Lazy>} />
        <Route path="/admin-sign-in" element={<SignIn />} />  
        <Route path="/dashboard" element={<Lazy><Dashboard /></Lazy>} />
        <Route path="/dashboard/add-job-post" element={<Lazy><AddJobPost /></Lazy>} />
        <Route path="/dashboard/add-team-member" element={<Lazy><AddTeamMember /></Lazy>} />
        <Route path="/dashboard/add-event-gallery" element={<Lazy><AddEventGallery /></Lazy>} />
        <Route path="/dashboard/add-case-study" element={<Lazy><AddCaseStudy /></Lazy>} />
        <Route path="/dashboard/add-testimonial" element={<Lazy><AddTestimonial /></Lazy>} />
        <Route path="/dashboard/add-tips" element={<Lazy><AddTips /></Lazy>} />
        <Route path="/dashboard/resume-received" element={<Lazy><ResumeReceived /></Lazy>} />
        <Route path="/dashboard/appointments-received" element={<Lazy><AppointmentsReceived /></Lazy>} />
        <Route path="/dashboard/subscribers" element={<Lazy><NewsletterSubscribers /></Lazy>} /> 
         <Lazy><Dashboard /></Lazy>
        */}


<Route path="/login" element={<Lazy><SignInPage /></Lazy>} />

<Route
  path="/dashboard"
  element={
    <ProtectedRoute>
      <Lazy><Dashboard /></Lazy>
    </ProtectedRoute>
  }
/> 
<Route
  path="/dashboard/add-job-post"
  element={
    <ProtectedRoute>
      <Lazy><AddJobPost /></Lazy>
    </ProtectedRoute>
  }
/>
<Route
  path="/dashboard/add-team-member"
  element={
    <ProtectedRoute>
      <Lazy><AddTeamMember /></Lazy>
    </ProtectedRoute>
  }
/>
<Route
  path="/dashboard/add-event-gallery"
  element={
    <ProtectedRoute>
      <Lazy><AddEventGallery /></Lazy>
    </ProtectedRoute>
  }
/>
<Route
  path="/dashboard/add-case-study"
  element={
    <ProtectedRoute>
      <Lazy><AddCaseStudy /></Lazy>
    </ProtectedRoute>
  }
/>
<Route
  path="/dashboard/add-testimonial"
  element={
    <ProtectedRoute>
      <Lazy><AddTestimonial /></Lazy>
    </ProtectedRoute>
  }
/>
<Route
  path="/dashboard/add-tips"
  element={
    <ProtectedRoute>
      <Lazy><AddTips /></Lazy>
    </ProtectedRoute>
  }
/>
<Route
  path="/dashboard/add-faq"
  element={
    <ProtectedRoute>
      <Lazy><AddFaq /></Lazy>
    </ProtectedRoute>
  }
/>
<Route
  path="/dashboard/resume-received"
  element={
    <ProtectedRoute>
      <Lazy><ResumeReceived /></Lazy>
    </ProtectedRoute>
  }
/>
<Route
  path="/dashboard/appointments-received"
  element={
    <ProtectedRoute>
      <Lazy><AppointmentsReceived /></Lazy>
    </ProtectedRoute>
  }
/>
<Route
  path="/dashboard/subscribers"
  element={
    <ProtectedRoute>
      <Lazy><NewsletterSubscribers /></Lazy>
    </ProtectedRoute>
  }
/>
<Route
  path="/dashboard/add-portfolio"
  element={
    <ProtectedRoute>
     <Lazy><AddPortfolio /></Lazy>
    </ProtectedRoute>
  }
/> 
        {/* Catch-all Route for unmatched URLs */}
       <Route path="*" element={<NotFound />} />
      </Routes>
      </ErrorBoundary>
      </NotFoundStatusContext.Provider>
    </Router>
  );
}



export default App;